// German is the source language of the UI. Enum vocabularies live here; view
// texts are wrapped in t(<German source>) and translated through the dictionary
// installed by i18n-en.ts (keys are the German source strings).

let dictionary: Record<string, string> | null = null;
let intlLocale = 'de-AT';

export function installDictionary(texts: Record<string, string>, locale: string): void {
  dictionary = texts;
  intlLocale = locale;
  dateFormat = new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  dateTimeFormat = new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function currentIntlLocale(): string {
  return intlLocale;
}

/** Marks a German source text for extraction when it is translated later via t(variable). */
export const tk = (text: string): string => text;

/** Translate a German source text; `{name}` placeholders are filled from vars. */
export function t(text: string, vars?: Record<string, unknown>): string {
  const template = dictionary?.[text] ?? text;
  return vars ? template.replace(/\{(\w+)\}/g, (match, key: string) => (key in vars ? String(vars[key] ?? '') : match)) : template;
}


export const TASK_STATUS: Record<string, string> = {
  todo: 'Offen',
  in_progress: 'In Arbeit',
  blocked: 'Blockiert',
  done: 'Erledigt',
  cancelled: 'Abgebrochen',
};
export const PRIORITY: Record<string, string> = { low: 'Niedrig', medium: 'Mittel', high: 'Hoch', urgent: 'Dringend' };
export const PROJECT_STATUS: Record<string, string> = {
  proposed: 'Angebot',
  active: 'Aktiv',
  on_hold: 'Pausiert',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
};
export const HEALTH: Record<string, string> = { green: 'Im Plan', amber: 'Gefährdet', red: 'Kritisch' };
export const RISK_STATUS: Record<string, string> = {
  open: 'Offen',
  mitigating: 'In Behandlung',
  accepted: 'Akzeptiert',
  closed: 'Geschlossen',
  occurred: 'Eingetreten',
};
export const RISK_RESPONSE: Record<string, string> = {
  avoid: 'Vermeiden',
  mitigate: 'Mindern',
  transfer: 'Übertragen',
  accept: 'Akzeptieren',
};
export const RISK_CATEGORY: Record<string, string> = {
  technical: 'Technisch',
  operational: 'Betrieb',
  financial: 'Finanziell',
  compliance: 'Compliance',
  strategic: 'Strategisch',
  market: 'Markt',
  reputational: 'Reputation',
  legal: 'Rechtlich',
  security: 'Sicherheit',
  schedule: 'Termin',
  resource: 'Ressourcen',
  quality: 'Qualität',
  project: 'Projekt',
};
export const PROBABILITY: Record<number, string> = { 1: 'Sehr selten', 2: 'Selten', 3: 'Möglich', 4: 'Wahrscheinlich', 5: 'Häufig' };
export const IMPACT: Record<number, string> = { 1: 'Gering', 2: 'Spürbar', 3: 'Erheblich', 4: 'Kritisch', 5: 'Katastrophal' };
export const DECISION_STATUS: Record<string, string> = {
  proposed: 'Vorgeschlagen',
  approved: 'Beschlossen',
  rejected: 'Abgelehnt',
  superseded: 'Überholt',
};
export const GATE_STATUS: Record<string, string> = {
  pending: 'Ausstehend',
  passed: 'Bestanden',
  failed: 'Nicht bestanden',
  waived: 'Mit Ausnahme',
};
export const PHASE_STATUS: Record<string, string> = { planned: 'Geplant', active: 'Aktiv', done: 'Abgeschlossen', skipped: 'Übersprungen' };
export const NOTE_KIND: Record<string, string> = { note: 'Notiz', meeting: 'Besprechung', lessons_learned: 'Lessons Learned' };
export const COMPANY_KIND: Record<string, string> = {
  client: 'Kunde',
  supplier: 'Lieferant',
  partner: 'Partner',
  own: 'Eigene Firma',
  other: 'Sonstige',
};
export const ROLE: Record<string, string> = { owner: 'Inhaber', admin: 'Admin', member: 'Mitglied', guest: 'Gast' };
export const ENTITY: Record<string, string> = {
  project: 'Projekt',
  task: 'Aufgabe',
  risk: 'Risiko',
  decision: 'Entscheidung',
  note: 'Notiz',
  gate: 'Gate',
  gate_criterion: 'Gate-Kriterium',
  phase: 'Phase',
  stakeholder: 'Stakeholder',
  kpi: 'Kennzahl',
  company: 'Firma',
  contact: 'Kontakt',
  absence: 'Abwesenheit',
  webhook: 'Webhook',
  api_token: 'API-Token',
  organization: 'Organisation',
  membership: 'Mitgliedschaft',
  project_member: 'Projektfreigabe',
  connector: 'Connector',
};
export const ACTION: Record<string, string> = {
  created: 'angelegt',
  updated: 'geändert',
  deleted: 'gelöscht',
  decided: 'entschieden',
  measured: 'gemessen',
  revoked: 'widerrufen',
  test: 'getestet',
  analysed: 'vom Assistenten ausgewertet',
  linked: 'verknüpft',
  unlinked: 'Verknüpfung entfernt',
  commented: 'kommentiert',
  comment_deleted: 'Kommentar gelöscht',
};

export function options(map: Record<string | number, string>, selected?: unknown, includeEmpty?: string): string {
  const escape = (v: unknown) => String(v).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  const empty = includeEmpty !== undefined ? `<option value="">${escape(includeEmpty)}</option>` : '';
  return (
    empty +
    Object.entries(map)
      .map(([value, label]) => `<option value="${escape(value)}"${String(selected) === value ? ' selected' : ''}>${escape(label)}</option>`)
      .join('')
  );
}

let dateFormat = new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
let dateTimeFormat = new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export function formatDate(value: string | null | undefined): string {
  if (!value) return '–';
  const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
  return dateFormat.format(date);
}

export function formatDateTime(value: string | null | undefined): string {
  return value ? dateTimeFormat.format(new Date(value)) : '–';
}

export function formatMoney(value: number | null | undefined, currency = 'EUR'): string {
  if (value === null || value === undefined) return '–';
  return new Intl.NumberFormat(intlLocale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

export function today(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function isOverdue(due: string | null | undefined): boolean {
  return !!due && due < today();
}
